import HumanizeDuration from "humanize-duration";
import moment from "moment";
// eslint-disable-next-line sort-imports
import i18n from "@/i18n";

/**
 * @summary Data atual. Em horário local usuário.
 * Mon Sep 07 2020 20:17:44 GMT-0300 (Horário Padrão de Brasília) {}
 */
const TO_DAY = new Date();

/**
 * @summary Data de 1 dia atrás em relação ao dia de hoje. Em horario local do usuário.
 * Formato: Mon Sep 07 2020 20:17:44 GMT-0300 (Horário Padrão de Brasília) {}
 */
const DAY_BEFORE = moment(TO_DAY).subtract(1, "day");

/**
 * @summary Data de 7 dias atrás em relação ao dia de hoje. Em horario local do usuário.
 * Formato: Mon Sep 07 2020 20:17:44 GMT-0300 (Horário Padrão de Brasília) {}
 */
const WEEK_BEFORE = moment(TO_DAY).subtract(7, "day");

/**
 * @summary Data de 7 dias atrás em relação ao dia de hoje. Em horario local do usuário.
 * Formato: Mon Sep 07 2020 20:17:44 GMT-0300 (Horário Padrão de Brasília) {}
*/
const MONTH_BEFORE = moment(TO_DAY).subtract(30, "day");

// Função para formatar a data para MMM/YY.
// Entrada tipo Date.
// Retorna uma string da data no tipo MMM/YY.
// Exemplo: 08/Jan
const ToMMMYY = date => moment(date).utc().local(true)
	.locale(i18n.locale)
	.format("MMM/YY");

/**
 * Transforma Date UTC em Date UTC -offset do usuário.
 * @param {Date} date - Data em UTC
 * @returns Data com offset do usuário.
 */
const ToLocalTime = (date) =>
{
	// Busca qual offset é do usuário.
	const offset = new Date().getTimezoneOffset();

	// Valor negativo para subtrair a diferença do usuário.
	// BR é 180 graus, logo tem que diminuir 180 graus.
	return moment(date).utc(true).utcOffset(-offset).format();
};

/**
	* @summary Formata a data UTC  em Local "DD/MM/YYYY " de UTC
	* @type {Date - date} Data em UTC"
	* @type {boolean - keepLocalTime} Se deve ou não manter o local time quando for feito o parse.
	* @returns {string} - Data Local em  "DD/MM/YYYY"
*/
const ToDDMMYYYY = (date, keepLocalTime = false) => moment(date).utc(keepLocalTime).local().format("DD/MM/YYYY");

const FormatDDMMYYYYToVuetify = (date) =>
{
	const day = date.slice(0, 2);
	const month = date.slice(3, 5);
	const year = date.slice(6, 10);
	return `${year}-${month}-${day}`;
};

/**
	* @summary Formata a data UTC  em Local "DD/MM/YYYY " de UTC
	* @type {Date - date} Data em UTC"
	* @type {boolean - keepLocalTime} Se deve ou não manter o local time quando for feito o parse.
	* @returns {string} - Data Local em  "DD/MM/YYYY HH:MM:SS"
*/
const ToDDMMYYYYHHMMSS = (date, keepLocalTime = true) => moment(date).utc(keepLocalTime).local()
	.format("DD/MM/YYYY HH:mm:ss");

// <summary>
// Função para saber o número de dias em um ano.
// </summary>
// <param name="year"> Ano a qual deseja se saber o número de dias.</param>
// <returns>Retorna o número de dias do ano</returns>
const DaysInYear = (year) =>
{
	const months = [ "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12" ];

	// Utiliza um map para retornar um array com número de dia em cada mês do ano.
	const daysInYear = months
		.map((month) =>
		{
			const yearMonth = `${year}-${month}`;

			// Utiliza a biblioteca moment para pegar o números de dia em um mês.
			const daysInMonth = moment(yearMonth, "YYYY-MM").daysInMonth();

			return daysInMonth;
		})
		.reduce((totalDays, daysInMonth) => totalDays + daysInMonth);
	return daysInYear;
};

/**
	* @summary Formata a data em "DD/MM/YYYY HH:mm:ss" para UTC. Remove o indicador de Zero Hour Offset do momentjs.
	* @type {function}
	* @param {Date - date} Data valor local da data no formato "DD/MM/YYYY HH:mm:ss"
	* @param {boolean - keepLocalTime} Se deve ou não manter o local time quando for feito o parse.
	* @returns {string} - Transforma data local(input) em data formatada em utc ISO 8601 strings.
*/
const DDMMYYYYHHMMToUTC = (date, keepLocalTime = false) => moment(date, "DD/MM/YYYY HH:mm:ss")
	.utc(keepLocalTime)
	.format().replace("Z", "");

/**
	* @summary Formata a data em "Anos, Meses, Dias, Horas, Minutos, Segundos".
	* @type {value} Valor de período em segundos.
	* @returns {string} - String formatada.
*/
const SecondsToDateFormat = (value) =>
{
	const portugueseHumanizer = HumanizeDuration.humanizer({
		language: i18n.locale === "pt-BR" ? "pt" : "en-US",
		units:    [ "y", "mo", "d", "h", "m", "s" ],
	});
	const formattedMiliseconds = portugueseHumanizer(value * 1000, { round: true });
	return formattedMiliseconds;
};

/**
	* @summary Formata um período, em segundos, no formato "X dias".
	* @type {value} Valor de período em segundos.
	* @returns {string} - String formatada.
*/
const SecondsToDayFormat = (value) =>
{
	const portugueseHumanizer = HumanizeDuration.humanizer({
		language: i18n.locale === "pt-BR" ? "pt" : "en-US",
		units:    [ "d" ],
	});
	const formattedMiliseconds = portugueseHumanizer(value * 1000, { round: true });
	return formattedMiliseconds;
};

/**
	* @summary Retorna a diferença de tempo entre um timestamp específico e o momento presente, em minutos.
	* @type {String} Timestamp a ser analisado.
	* @returns {Number} - Diferença de tempo, em minutos.
*/
const TimeDifferenceInMinutesFromNow = (date) =>
{
	const dateTimeReference = moment(date, "YYYY-MM-DDTHH:mm:ss");
	const now = moment(moment.utc().format(), "YYYY-MM-DDTHH:mm:ss");

	return moment.duration(now.diff(dateTimeReference)).asMinutes();
};

/**
 * Retorna uma data com n dia/mês/ano da data atual.
 * @param {int} n Quantidade de dias ou mês ou ano a ser obtida a data.
 * @param {String} from Parâmetro de período (days/months/years)
 */
const GetPreviusDate = (n, from) =>
{
	switch (from)
	{
	case "days":
		return DDMMYYYYHHMMToUTC(moment(TO_DAY).subtract(n, "day"));
	case "months":
		return DDMMYYYYHHMMToUTC(moment(TO_DAY).subtract(n, "month"));
	case "years":
		return DDMMYYYYHHMMToUTC(moment(TO_DAY).subtract(n, "year"));
	default:
		return "";
	}
};

/**
 * @summary Data de 30 dias atrás no formato yyyy-mm-dd.
*/
const START_DATE = moment(TO_DAY)
	.subtract(30, "day")
	.toISOString()
	.substr(0, 10);

/**
 * @summary Data de hoje no formato yyyy-mm-dd.
*/
const END_DATE = moment(TO_DAY)
	.toISOString()
	.substr(0, 10);

/**
* @summary Horário local em HH:mm
*/
const TIME_NOW = moment(TO_DAY).format("HH:mm");

/**
* @summary Data de 30 dias atrás em relação ao dia de hoje (Formato DD/MM/AAAA).
*/
const HUMAN_START_DATE = ToDDMMYYYY(START_DATE);

/**
* @summary Data de 30 dias atrás em relação ao dia de hoje (Formato DD/MM/AAAA).
*/
const HUMAN_END_DATE = ToDDMMYYYY(END_DATE);

/**
 * @summary Dia anterior ao atual no padrão DD/MM/YYYY em data local.
 */
const HUMAN_DAY_BEFORE = ToDDMMYYYY(DAY_BEFORE);

/**
 * @summary 7 dias anteriores ao atual no padrão DD/MM/YYYY em data local.
 */
const HUMAN_WEEK_BEFORE = ToDDMMYYYY(WEEK_BEFORE);

/**
 * @summary 30 dias anteriores ao atual no padrão DD/MM/YYYY em data local.
*/
const HUMAN_MONTH_BEFORE = ToDDMMYYYY(MONTH_BEFORE);

/**
* @summary Data e hora 10 minutos atrás em relação ao dia de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_TEN_MINUTES_BEFORE = DDMMYYYYHHMMToUTC(moment(TO_DAY).subtract(10, "minutes"));

/**
* @summary Data e hora de 30 dias atrás em relação ao dia de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_DAY_BEFORE = DDMMYYYYHHMMToUTC(DAY_BEFORE);

/**
* @summary Data e hora de 30 dias atrás em relação ao dia de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_WEEK_BEFORE = DDMMYYYYHHMMToUTC(WEEK_BEFORE);

/**
* @summary Data e hora de 30 dias atrás em relação ao dia de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_MONTH_BEFORE = DDMMYYYYHHMMToUTC(MONTH_BEFORE);

/**
* @summary Data e hora de 30 dias atrás em relação ao dia de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_START_DATE_TIME = API_MONTH_BEFORE;

/**
* @summary Data e hora de hoje (Formato DD-MM-YYYYY HH:MM) UTC.
*/
const API_END_DATE_TIME = DDMMYYYYHHMMToUTC(TO_DAY);

const CreateFirstPeriod = () =>
{
	const period = {
		startDate: ToDDMMYYYY(GetPreviusDate(1, "days")),
		endDate:   ToDDMMYYYY(TO_DAY),
		startTime: moment(new Date()).format("HH:mm"),
		endTime:   moment(new Date()).format("HH:mm"),
	};
	return period;
};

const FormatToAudioPlayer = second => new Date(second * 1000).toISOString().substr(11, 8);

/**
 * @summary Converte data em yyyy-mm-dd ex: 2020-10-20.
 * @param {Date - String} date - Data a qual deseja ser formatada.
 * @returns data no formato yyyy-mm-dd.
 */
const ToYYYYMMDD = date => moment(date).toISOString()
	.substr(0, 10);

export default {
	moment,
	ToMMMYY,
	ToDDMMYYYY,
	ToDDMMYYYYHHMMSS,
	ToLocalTime,
	GetPreviusDate,
	DDMMYYYYHHMMToUTC,
	DaysInYear,
	SecondsToDateFormat,
	SecondsToDayFormat,
	TO_DAY,
	START_DATE,
	TIME_NOW,
	END_DATE,
	FormatDDMMYYYYToVuetify,
	FormatToAudioPlayer,
	HUMAN_START_DATE,
	HUMAN_END_DATE,
	API_START_DATE_TIME,
	API_END_DATE_TIME,
	API_DAY_BEFORE,
	API_MONTH_BEFORE,
	API_WEEK_BEFORE,
	HUMAN_DAY_BEFORE,
	HUMAN_WEEK_BEFORE,
	HUMAN_MONTH_BEFORE,
	API_TEN_MINUTES_BEFORE,
	TimeDifferenceInMinutesFromNow,
	CreateFirstPeriod,
	ToYYYYMMDD,
};
